import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { AnimationGroupMask, AnimationGroupMaskMode } from '@babylonjs/core/Animations/animationGroupMask';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { CharacterConfig } from '../config/character.config';
import { baseShare, bodyRegions, clipFallback, fadeSecondsFor, isLooping, isRunCycle, layerOf, maskOf } from '../characters/characterAnimation';
import { ALL_CHARACTER_CLIPS, CharacterClip, ClipLayer, type CharacterDefinition } from '../characters/CharacterTypes';

/** One body region of a clip: its AnimationGroup restricted to the region's bones. */
interface Part {
  readonly group: AnimationGroup;
  /** Layered clips that push this part back while they play (none for layered clips themselves). */
  readonly layers: ReadonlySet<CharacterClip>;
}

/** A playable clip: its parts always run together on the same frame range. */
interface Clip {
  readonly parts: readonly Part[];
  readonly from: number;
  readonly to: number;
  /** Groups created here (region clones) – disposed with the animator. */
  readonly owned: readonly AnimationGroup[];
}

interface Track {
  readonly clip: CharacterClip;
  readonly playback: Clip;
  /** Started by trigger() on top of the base clip. */
  readonly overlay: boolean;
  weight: number;
  target: number;
  /** Weight change per second. */
  rate: number;
  /** End listener of a one-shot playback (removed when the track is stopped early). */
  onEnd: Observer<AnimationGroup> | null;
}

/** Fade time used when nothing was playing yet (no blend from the bind pose). */
const INSTANT = 0;
const MIN_FADE_SECONDS = 1e-3;
const NO_LAYERS: ReadonlySet<CharacterClip> = new Set();

/** Strips the "<instance>:" prefix the visual factory gives cloned nodes. */
function boneName(target: unknown): string {
  const name = (target as { name?: string }).name ?? '';
  return name.slice(name.lastIndexOf(':') + 1);
}

/**
 * Frame range of the authored keys. Blender exports a clip whose first key sits on frame 1 (1/30 s);
 * the glTF loader then normalizes the group to start at 0 by inserting a copy of that first key
 * (same value object). Playing from 0 would hold the first pose for a moment on every loop – the
 * planted foot slides along with the body at the loop seam – so those inserted keys are skipped.
 */
export function keyRange(group: AnimationGroup): { from: number; to: number } {
  let from = Infinity;
  let to = -Infinity;
  for (const { animation } of group.targetedAnimations) {
    const keys = animation.getKeys();
    if (keys.length === 0) continue;
    const inserted = keys.length > 1 && keys[0]!.value === keys[1]!.value && keys[0]!.frame < keys[1]!.frame;
    from = Math.min(from, keys[inserted ? 1 : 0]!.frame);
    to = Math.max(to, keys[keys.length - 1]!.frame);
  }
  return from < to ? { from, to } : { from: group.from, to: group.to };
}

/** Keeps only the channels of the given bones – masked-out channels are dropped for good (no parked animatables). */
function restrict(group: AnimationGroup, keep: (bone: string) => boolean): void {
  const names = group.targetedAnimations.map((t) => (t.target as { name?: string }).name ?? '').filter((name) => keep(boneName({ name })));
  group.mask = new AnimationGroupMask(names, AnimationGroupMaskMode.Include);
  group.removeUnmaskedAnimations();
}

/**
 * Plays a rigged character's clips (cloned Babylon AnimationGroups):
 * one continuous base clip chosen from the player state, cross-faded on change,
 * plus one-shot reactions layered on top (upper body or full body). Non-looping
 * base clips hold their last frame (jump apex, slide, game over).
 * Babylon caps every animation weight at 1 and normalizes sums above 1, so a reaction cannot
 * simply weigh more: full-body clips are split into body regions and a reaction pushes the
 * base clip back (`overlayDominance`) in exactly the regions it drives.
 */
export class CharacterAnimator {
  private base: Track | null = null;
  private overlay: Track | null = null;
  private readonly fading: Track[] = [];
  /**
   * One-shots that ended and must hold their last frame. Restarted in the next update(), never inside
   * Babylon's end notification: the group clears its animatable list right after notifying, which
   * would orphan animatables started from the observer (they keep posing the bones forever).
   */
  private readonly holds: Track[] = [];
  private paused = false;

  private constructor(
    private readonly clips: ReadonlyMap<CharacterClip, Clip>,
    private readonly config: CharacterConfig,
  ) {}

  /** Null when the model has no usable clips (greybox fallback, unrigged placeholder). */
  static create(groups: readonly AnimationGroup[], character: CharacterDefinition, config: CharacterConfig): CharacterAnimator | null {
    const byClip = new Map<CharacterClip, AnimationGroup>();
    for (const clip of ALL_CHARACTER_CLIPS) {
      const name = character.clips[clip];
      // Clones are named "<instance>:<clip>" by the visual factory.
      const group = name ? groups.find((g) => g.name === name || g.name.endsWith(`:${name}`)) : undefined;
      if (group) byClip.set(clip, group);
    }
    if (!byClip.has(CharacterClip.Run)) return null;

    const regions = bodyRegions(config);
    const layered = new Set(regions.flatMap((r) => [...(r.bones ?? [])]));
    const clips = new Map<CharacterClip, Clip>();
    for (const [clip, group] of byClip) {
      group.stop(true);
      group.weight = 0;
      const range = keyRange(group);
      if (layerOf(clip) === ClipLayer.UpperBody) {
        const bones = new Set(maskOf(clip, config));
        restrict(group, (bone) => bones.has(bone));
        clips.set(clip, { ...range, parts: [{ group, layers: NO_LAYERS }], owned: [] });
        continue;
      }
      // The source group stays untouched (a later create() on the same visual splits it again).
      const parts: Part[] = [];
      const owned: AnimationGroup[] = [];
      regions.forEach((region, i) => {
        const part = group.clone(`${group.name}#${i}`);
        part.stop(true);
        part.weight = 0;
        owned.push(part);
        const { bones } = region;
        restrict(part, bones ? (bone) => bones.has(bone) : (bone) => !layered.has(bone));
        if (part.targetedAnimations.length > 0) parts.push({ group: part, layers: region.layers });
      });
      clips.set(clip, { ...range, parts, owned });
    }
    return new CharacterAnimator(clips, config);
  }

  /** Continuous clip for the current state – cross-fades when it changes. */
  setBase(clip: CharacterClip, speedRatio: number): void {
    const resolved = this.resolve(clip);
    if (this.base?.clip !== resolved) {
      const fade = this.base ? fadeSecondsFor(resolved, this.config) : INSTANT;
      // Run ↔ RunFast: keep the step phase, or the legs scissor during the cross-fade.
      const phase = this.base && isRunCycle(this.base.clip) && isRunCycle(resolved) ? this.phaseOf(this.base) : null;
      if (this.base) this.fadeOut(this.base, fade);
      this.base = this.begin(resolved, false, fade);
      if (phase !== null) this.seek(this.base, phase);
    }
    if (isLooping(this.base.clip)) for (const part of this.base.playback.parts) part.group.speedRatio = speedRatio;
  }

  /** Position of a looping track in its cycle (0..1). */
  private phaseOf(track: Track): number {
    const frame = track.playback.parts[0]?.group.animatables[0]?.masterFrame;
    const span = track.playback.to - track.playback.from;
    if (frame === undefined || span <= 0) return 0;
    return ((((frame - track.playback.from) % span) + span) % span) / span;
  }

  private seek(track: Track, phase: number): void {
    const frame = track.playback.from + phase * (track.playback.to - track.playback.from);
    for (const { group } of track.playback.parts) group.goToFrame(frame);
  }

  /** One-shot reaction on top of the base clip; it fades out again when it ends. */
  trigger(clip: CharacterClip): void {
    if (!this.clips.has(clip)) return;
    if (this.overlay) this.fadeOut(this.overlay, this.config.quickFadeSeconds);
    this.overlay = this.begin(clip, true, this.config.quickFadeSeconds);
  }

  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    for (const track of this.tracks()) {
      for (const { group } of track.playback.parts) {
        if (paused) group.pause();
        else group.restart();
      }
    }
  }

  update(dt: number): void {
    if (this.paused) return;
    this.startHolds();
    const tracks = this.tracks();
    for (const track of tracks) {
      const step = track.rate * dt;
      track.weight = track.weight < track.target ? Math.min(track.target, track.weight + step) : Math.max(track.target, track.weight - step);
    }
    const overlays = tracks.filter((t) => t.overlay);
    for (const track of tracks) {
      for (const part of track.playback.parts) {
        part.group.weight = track.overlay ? track.weight : track.weight * baseShare(this.cover(part, overlays), this.config);
      }
    }
    for (let i = this.fading.length - 1; i >= 0; i--) {
      const track = this.fading[i];
      if (track && track.weight <= 0) {
        this.stop(track);
        this.fading.splice(i, 1);
      }
    }
  }

  /** New run: everything stopped, the next setBase() starts cleanly. */
  reset(): void {
    for (const track of this.tracks()) this.stop(track);
    this.fading.length = 0;
    this.holds.length = 0;
    this.base = null;
    this.overlay = null;
    this.paused = false;
  }

  dispose(): void {
    this.reset();
    for (const clip of this.clips.values()) for (const group of clip.owned) group.dispose();
  }

  /** Summed weight of the reactions playing on a part's bones. */
  private cover(part: Part, overlays: readonly Track[]): number {
    let weight = 0;
    for (const o of overlays) if (layerOf(o.clip) === ClipLayer.FullBody || part.layers.has(o.clip)) weight += o.weight;
    return weight;
  }

  /** The model has its own clip for `clip` (not a fallback). */
  has(clip: CharacterClip): boolean {
    return this.clips.has(clip);
  }

  private resolve(clip: CharacterClip): CharacterClip {
    if (this.clips.has(clip)) return clip;
    const fallback = clipFallback(clip);
    return this.clips.has(fallback) ? fallback : CharacterClip.Run;
  }

  private begin(clip: CharacterClip, overlay: boolean, fadeSeconds: number): Track {
    const playback = this.clips.get(clip);
    if (!playback) throw new Error(`clip ${clip} missing`); // resolve()/trigger() only pass existing clips
    // The same clip may still be fading out – take it over instead of playing it twice.
    const index = this.fading.findIndex((t) => t.playback === playback);
    const previous = index >= 0 ? this.fading[index] : undefined;
    if (previous) {
      this.fading.splice(index, 1);
      this.stop(previous);
    }
    const startWeight = fadeSeconds <= INSTANT ? 1 : (previous?.weight ?? 0);
    const loop = isLooping(clip);
    for (const { group } of playback.parts) {
      group.weight = startWeight;
      group.start(loop, 1, playback.from, playback.to);
      if (this.paused) group.pause();
    }
    const track: Track = { clip, playback, overlay, weight: startWeight, target: 1, rate: 1 / Math.max(fadeSeconds, MIN_FADE_SECONDS), onEnd: null };
    // All parts run the same range in lockstep – the first one reports the end.
    const lead = playback.parts[0]?.group;
    if (!loop && lead) track.onEnd = lead.onAnimationGroupEndObservable.addOnce(() => this.ended(track));
    return track;
  }

  /** A one-shot finished: overlays fade out, base clips hold their last frame. */
  private ended(track: Track): void {
    track.onEnd = null;
    if (track === this.overlay) {
      this.overlay = null;
      this.fadeOut(track, this.config.fadeSeconds);
    }
    if (track.target > 0 || track.weight > 0) this.holds.push(track);
  }

  /** Holds the last frame of ended one-shots that are still visible (base clip, or a layer fading out). */
  private startHolds(): void {
    for (const track of this.holds.splice(0)) {
      const live = track === this.base || track === this.overlay || this.fading.includes(track);
      const { parts, to } = track.playback;
      if (!live || parts.some((p) => p.group.isStarted)) continue;
      for (const { group } of parts) group.start(true, 1, to, to);
    }
  }

  private fadeOut(track: Track, fadeSeconds: number): void {
    track.target = 0;
    track.rate = Math.max(track.weight, 1) / Math.max(fadeSeconds, MIN_FADE_SECONDS);
    if (!this.fading.includes(track)) this.fading.push(track);
  }

  private stop(track: Track): void {
    const pending = this.holds.indexOf(track);
    if (pending >= 0) this.holds.splice(pending, 1);
    const lead = track.playback.parts[0]?.group;
    if (track.onEnd && lead) lead.onAnimationGroupEndObservable.remove(track.onEnd);
    track.onEnd = null;
    for (const { group } of track.playback.parts) {
      group.stop(true);
      group.weight = 0;
    }
  }

  private tracks(): Track[] {
    const all = [...this.fading];
    if (this.base) all.push(this.base);
    if (this.overlay) all.push(this.overlay);
    return all;
  }
}
